import {
  QResourceRoutes,
  QualioQThemeProvider,
  ToastProvider,
} from '@qualio/ui-components';
import { CurrentUser } from '@qualio/ui-components/lib/types/CurrentUser';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';
import { globalSearchServiceApiClient } from './api/globalSearchService';
import { medtechApiClient } from './api/index';
import { qriApiClient, setQriApiClientBaseUrl } from './api/qri';
import styles from './App.module.css';
import { useQriServiceUrl } from './hooks/useQriServiceUrl';
import { ProtectedRoute } from './ProtectedRoute';
import { BulkFileUploadRoot } from './views/components/BulkFileUpload/BulkFileUploadRoot';
import { DocumentCompareVersions } from './views/components/DocumentCompareVersions';
import { DocumentLibrary, LibraryV2 } from './views/components/DocumentLibrary';
import { DocumentOverview } from './views/components/DocumentOverview/DocumentOverview';
import { TrainingAssessmentView } from './views/components/DocumentOverview/DocumentTraining';
import { OverviewRoutes } from './views/components/DocumentOverview/routes';

import { AllDocumentsTab } from './views/components/DocumentWorkspace/WorkspaceV2/Tabs/AllDocuments/AllDocumentsTab';
import { YourActionsTab } from './views/components/DocumentWorkspace/WorkspaceV2/Tabs/YourActions/YourActionsTab';
import { YourDocumentsTab } from './views/components/DocumentWorkspace/WorkspaceV2/Tabs/YourDocuments/YourDocumentsTab';
import { WorkspaceV2 } from './views/components/DocumentWorkspace/WorkspaceV2/WorkspaceV2';
import { EmbedDocument } from './views/components/Embed/EmbedDocument';
import { ErrorHandlerProvider } from './views/components/ErrorHandler';
import { NotFound } from './views/components/NotFound';

export const App: React.FC<{
  currentUser: CurrentUser;
}> = ({ currentUser }) => {
  const { newLibraryTable, documentsTaskDrawer, dcLinkResourceLibraryProduct } =
    useFlags();
  const qriServiceUrl = useQriServiceUrl();
  setQriApiClientBaseUrl(qriServiceUrl);

  const basePaths = [
    '/workspace/e/:id/approve-pr',
    '/workspace/e/:id/training',
    '/workspace/documents/:id/approve-pr',
    '/workspace/documents/:id/training',
    '/library/documents/:id/training',
    '/library/e/:id/training',
  ];

  const documentTabBasePaths = [
    '/document-editor/edit',
    '/workspace/e',
    '/workspace/documents',
    '/library/documents',
    '/library/e',
  ];

  const compareVersionsRoutePaths = [
    '/workspace/e/:id/compare/:baseCompare',
    '/workspace/documents/:id/compare/:baseCompare',
    '/library/documents/:id/compare/:baseCompare',
    '/library/e/:id/compare/:baseCompare',
    '/workspace/e/:id/training/compare/:baseCompare',
    '/workspace/documents/:id/training/compare/:baseCompare',
    '/library/documents/:id/training/compare/:baseCompare',
    '/library/e/:id/training/compare/:baseCompare',
  ];

  const trainingAssessmentRoutePaths = [
    '/workspace/e/:id/training/assessment',
    '/workspace/documents/:id/training/assessment',
    '/library/documents/:id/training/assessment',
    '/library/e/:id/training/assessment',
  ];

  const fileUploadRoute = '/upload';
  const libraryRoute = '/library';
  const workspaceRoute = '/workspace';
  const appClasses = `${styles['document-editor__app']}`;
  return (
    <div id="documentEditorApp" className={appClasses}>
      <ErrorHandlerProvider>
        <Router useTransitions={false}>
          <Routes>
            {compareVersionsRoutePaths.map((path) => {
              return (
                <Route
                  key={path}
                  path={path}
                  element={
                    <QualioQThemeProvider>
                      <ToastProvider enableDelegation={true}>
                        <DocumentCompareVersions />
                      </ToastProvider>
                    </QualioQThemeProvider>
                  }
                />
              );
            })}
            {trainingAssessmentRoutePaths.map((path) => {
              return (
                <Route
                  key={path}
                  path={path}
                  element={
                    <QualioQThemeProvider>
                      <ToastProvider enableDelegation={true}>
                        <TrainingAssessmentView />
                      </ToastProvider>
                    </QualioQThemeProvider>
                  }
                />
              );
            })}
            {documentTabBasePaths.map((path) => (
              <Route key={path} path={path}>
                {QResourceRoutes({
                  path: ':id',
                  mtbeClient: medtechApiClient,
                  qriClient: qriApiClient,
                  globalSearchClient: globalSearchServiceApiClient,
                  element: (
                    <QualioQThemeProvider>
                      <ToastProvider enableDelegation={true}>
                        <DocumentOverview />
                      </ToastProvider>
                    </QualioQThemeProvider>
                  ),
                  withTasks: documentsTaskDrawer,
                  withRelationships: true,
                  showDesignControlsProjectLabel: Boolean(
                    dcLinkResourceLibraryProduct,
                  ),
                  children: (
                    <>
                      <Route
                        index
                        element={
                          <Navigate to={OverviewRoutes.CONTENT} replace />
                        }
                      />
                      <Route path={OverviewRoutes.CONTENT} element={null} />
                      <Route
                        path={OverviewRoutes.CHANGE_CONTROL}
                        element={null}
                      />
                      <Route
                        path={OverviewRoutes.TRAINING_ASSESSMENT}
                        element={null}
                      />
                      <Route
                        path="training"
                        element={
                          <Navigate
                            to={`../${OverviewRoutes.CONTENT}`}
                            replace
                          />
                        }
                      />
                    </>
                  ),
                })}
              </Route>
            ))}
            {basePaths.map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <QualioQThemeProvider>
                    <ToastProvider enableDelegation={true}>
                      <DocumentOverview />
                    </ToastProvider>
                  </QualioQThemeProvider>
                }
              />
            ))}
            <Route
              path={fileUploadRoute}
              element={<BulkFileUploadRoot currentUser={currentUser} />}
            ></Route>
            <Route
              path={libraryRoute}
              element={
                <ProtectedRoute
                  predicate={() => currentUser.permissions.can_view_library}
                >
                  <QualioQThemeProvider>
                    <ToastProvider enableDelegation={true}>
                      {newLibraryTable ? <LibraryV2 /> : <DocumentLibrary />}
                    </ToastProvider>
                  </QualioQThemeProvider>
                </ProtectedRoute>
              }
            ></Route>
            <Route
              path={workspaceRoute}
              element={
                <ProtectedRoute
                  predicate={() => currentUser.permissions.can_view_workspace}
                >
                  <QualioQThemeProvider>
                    <ToastProvider enableDelegation={true}>
                      <WorkspaceV2 />
                    </ToastProvider>
                  </QualioQThemeProvider>
                </ProtectedRoute>
              }
            >
              <Route path="" element={<Navigate to="your-documents" />} />
              <Route
                path="your-documents"
                element={<YourDocumentsTab />}
              ></Route>
              <Route path="your-actions" element={<YourActionsTab />}></Route>
              <Route path="all-documents" element={<AllDocumentsTab />}></Route>
            </Route>
            <Route
              path=":companyId/embed/:id"
              element={<EmbedDocument />}
            ></Route>
            <Route path="*" element={<NotFound />}></Route>
          </Routes>
        </Router>
      </ErrorHandlerProvider>
    </div>
  );
};
